import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { TouchApp as KioskIcon } from '@mui/icons-material';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

interface AddKioskModalProps {
  open: boolean;
  onClose: () => void;
  onKioskAdded: () => void;
  editKiosk?: KioskData | null;
}

interface KioskData {
  id: string;
  name: string;
  description?: string;
  branchId?: string | null;
  branchName?: string;
  status: 'active' | 'inactive' | 'maintenance';
  screenSize: '21inch' | '24inch' | '27inch';
  deviceId?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface BranchData {
  id: string;
  name: string;
}

interface KioskFormData {
  name: string;
  description: string;
  branchId: string;
  branchName: string;
  status: 'active' | 'inactive' | 'maintenance';
  screenSize: '21inch' | '24inch' | '27inch';
  deviceId: string;
}

const AddKioskModal: React.FC<AddKioskModalProps> = ({ open, onClose, onKioskAdded, editKiosk }) => {
  const { t } = useTranslation('salesSystem');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches: globalBranches } = useAuth();
  
  // AuthContext'ten gelen branches'i lokal BranchData formatına dönüştür
  const branches: BranchData[] = React.useMemo(() => {
    return globalBranches
      .filter(b => b.id !== 'null') // "Şubeye Ait Değil" seçeneğini filtrele
      .map(b => ({ id: b.id, name: b.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [globalBranches]);
  
  const isEditMode = !!editKiosk;
  const [formData, setFormData] = useState<KioskFormData>({
    name: '',
    description: '',
    branchId: '',
    branchName: '',
    status: 'active',
    screenSize: '21inch',
    deviceId: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('kioskAddModal.errors.userNotLoggedIn'));
        return;
      }
      
      try {
        console.log('🔍 [ADD KIOSK MODAL] Şirket ID alınıyor...', { userId: uid });
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData) {
          console.log('✅ [ADD KIOSK MODAL] Kullanıcı bilgileri alındı', { 
            companyId: userData.companyId,
            role: userData.role 
          });
          setCompanyId(userData.companyId || null);
        } else {
          setError(t('kioskAddModal.errors.userInfoNotFound'));
        }
      } catch (error) {
        console.error('❌ [ADD KIOSK MODAL] Şirket ID alınırken hata:', error);
        setError(t('kioskAddModal.errors.companyInfoLoadFailed'));
      }
    };
    
    if (open) {
      fetchCompanyId();
    }
  }, [open]);

  // Düzenleme modunda form verilerini doldur
  useEffect(() => {
    if (isEditMode && editKiosk && open) {
      setFormData({
        name: editKiosk.name || '',
        description: editKiosk.description || '',
        branchId: editKiosk.branchId || '',
        branchName: editKiosk.branchName || '',
        status: editKiosk.status || 'active',
        screenSize: editKiosk.screenSize || '21inch',
        deviceId: editKiosk.deviceId || ''
      });
    }
  }, [isEditMode, editKiosk, open]);

  // ✅ Şubeler artık AuthContext'ten otomatik geliyor

  const handleInputChange = (field: keyof KioskFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Şube seçildiğinde şube adını da güncelle
    if (field === 'branchId') {
      const selectedBranch = branches.find(b => b.id === value);
      setFormData(prev => ({
        ...prev,
        branchId: value,
        branchName: selectedBranch?.name || ''
      }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError(t('kioskAddModal.errors.nameRequired'));
      return false;
    }
    
    if (formData.name.length > 100) {
      setError(t('kioskAddModal.errors.nameMaxLength'));
      return false;
    }
    
    if (formData.description && formData.description.length > 500) {
      setError(t('kioskAddModal.errors.descriptionMaxLength'));
      return false;
    }
    
    if (formData.deviceId && formData.deviceId.length > 100) {
      setError(t('kioskAddModal.errors.deviceIdMaxLength'));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !companyId) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        branchId: formData.branchId || null,
        branchName: formData.branchName || null,
        status: formData.status,
        screenSize: formData.screenSize,
        deviceId: formData.deviceId.trim() || null,
        settings: null
      };

      if (isEditMode && editKiosk) {
        console.log('🔄 [KIOSK MODAL] Kiosk güncelleniyor...', { id: editKiosk.id, formData });
        response = await mainApi.post(`/api/Kiosk/${editKiosk.id}/update`, payload);
      } else {
        console.log('🔄 [KIOSK MODAL] Yeni kiosk oluşturuluyor...', formData);
        response = await mainApi.post(`/api/Kiosk`, payload);
      }

      const result = response.data;

      if (result.success) {
        console.log(`✅ [KIOSK MODAL] Kiosk başarıyla ${isEditMode ? 'güncellendi' : 'oluşturuldu'}:`, result.data);
        onKioskAdded();
        handleClose();
      } else {
        console.error(`❌ [KIOSK MODAL] Kiosk ${isEditMode ? 'güncelleme' : 'oluşturma'} başarısız:`, result.message);
        setError(result.message || t(isEditMode ? 'kioskAddModal.errors.updateFailed' : 'kioskAddModal.errors.createFailed'));
      }
    } catch (error) {
      console.error(`❌ [KIOSK MODAL] Kiosk ${isEditMode ? 'güncelleme' : 'oluşturma'} hatası:`, error);
      setError(t(isEditMode ? 'kioskAddModal.errors.updateFailed' : 'kioskAddModal.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        branchId: '',
        branchName: '',
        status: 'active',
        screenSize: '21inch',
        deviceId: ''
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <KioskIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">
          {isEditMode ? t('kioskAddModal.titleEdit') : t('kioskAddModal.titleCreate')}
        </Typography>
        <GuideButton guideKey="sales.addKiosk" size="small" />
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Kiosk Adı */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('kioskAddModal.fields.name')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              disabled={loading}
              inputProps={{ maxLength: 100 }}
              helperText={t('kioskAddModal.fields.charCount', { count: formData.name.length, max: 100 })}
            />
          </Grid>

          {/* Durum */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>{t('kioskAddModal.fields.status')}</InputLabel>
              <Select
                value={formData.status}
                label={t('kioskAddModal.fields.status')}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="active">{t('kioskAddModal.status.active')}</MenuItem>
                <MenuItem value="inactive">{t('kioskAddModal.status.inactive')}</MenuItem>
                <MenuItem value="maintenance">{t('kioskAddModal.status.maintenance')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Açıklama */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('kioskAddModal.fields.description')}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={2}
              disabled={loading}
              inputProps={{ maxLength: 500 }}
              helperText={t('kioskAddModal.fields.charCount', { count: formData.description.length, max: 500 })}
            />
          </Grid>

          {/* Şube */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={loading}>
              <InputLabel>{t('kioskAddModal.fields.branch')}</InputLabel>
              <Select
                value={formData.branchId}
                label={t('kioskAddModal.fields.branch')}
                onChange={(e) => handleInputChange('branchId', e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('kioskAddModal.fields.branchNotSelected')}</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Ekran Boyutu */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>{t('kioskAddModal.fields.screenSize')}</InputLabel>
              <Select
                value={formData.screenSize}
                label={t('kioskAddModal.fields.screenSize')}
                onChange={(e) => handleInputChange('screenSize', e.target.value)}
              >
                <MenuItem value="21inch">{t('kioskAddModal.screenSizes.21inch')}</MenuItem>
                <MenuItem value="24inch">{t('kioskAddModal.screenSizes.24inch')}</MenuItem>
                <MenuItem value="27inch">{t('kioskAddModal.screenSizes.27inch')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Cihaz ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('kioskAddModal.fields.deviceId')}
              value={formData.deviceId}
              onChange={(e) => handleInputChange('deviceId', e.target.value)}
              disabled={loading}
              inputProps={{ maxLength: 100 }}
              helperText={t('kioskAddModal.fields.charCount', { count: formData.deviceId.length, max: 100 })}
              placeholder={t('kioskAddModal.fields.deviceIdPlaceholder')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          {t('kioskAddModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <KioskIcon />}
        >
          {loading 
            ? (isEditMode ? t('kioskAddModal.actions.updating') : t('kioskAddModal.actions.creating'))
            : (isEditMode ? t('kioskAddModal.actions.update') : t('kioskAddModal.actions.create'))
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddKioskModal;
